import { Separator } from "@/components/ui/separator";

const SafetyGuidelines = () => {
  return (
    <div className="container mx-auto px-4 py-12 flex-grow max-w-4xl">
      <h1 className="font-poppins font-bold text-3xl text-anyskill-purple mb-8">
        Safety Guidelines & Community Standards
      </h1>
      
      <div className="prose prose-slate max-w-none">
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Our Community Values</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            AnySkill is built on trust, respect, and mutual growth. We're dedicated to creating a safe, 
            inclusive environment where students can share their skills and clients can find reliable help.
            Our platform thrives when everyone participates in creating a positive experience.
          </p>
        </section>
        
        <Separator className="my-8" />
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Service Offering Guidelines</h2>
          
          <h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Acceptable Services</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Academic tutoring in school subjects</li>
            <li>Creative skills like art, music, and design</li>
            <li>Technology skills such as coding and digital media</li>
            <li>Language learning and practice</li>
            <li>Study skills and academic coaching</li>
          </ul>
          
          <h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Prohibited Services</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Writing or completing assignments for others (academic dishonesty)</li>
            <li>Services that violate school policies or academic integrity</li>
            <li>Any illegal activities or services</li>
            <li>Services that pose safety or ethical concerns</li>
            <li>Services requiring professional licenses that you do not possess</li>
          </ul>
        </section>
        
        <Separator className="my-8" />
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Communication Standards</h2>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Communicate clearly and respectfully at all times</li>
            <li>Respond to messages within 48 hours when possible</li>
            <li>Keep all service-related communication on the platform</li>
            <li>Be professional and courteous in all interactions</li>
            <li>Respect boundaries and privacy in all communications</li>
          </ul>
        </section>
        
        <Separator className="my-8" />
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Payment Guidelines</h2>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>All payments must be processed through AnySkill's platform</li>
            <li>Never request or accept direct payments outside the platform</li>
            <li>Be transparent about your pricing structure</li>
            <li>Only charge for services actually delivered</li>
            <li>Respect the platform's refund policies</li>
          </ul>
        </section>
        
        <Separator className="my-8" />
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Safety Recommendations</h2>
          
          <h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">For Service Providers</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Meet in public places or online for initial consultations</li>
            <li>Get parental consent when working with minors</li>
            <li>Keep records of all service agreements</li>
            <li>Trust your instincts—if something feels uncomfortable, step back</li>
          </ul>
          
          <h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">For Clients</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Verify provider profiles and reviews before booking</li>
            <li>Be clear about your expectations and requirements</li>
            <li>Report any concerning behavior immediately</li>
            <li>Never share personal financial information</li>
          </ul>
        </section>
        
        <Separator className="my-8" />
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Reporting Violations</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            If you encounter any violation of these guidelines or feel unsafe:
          </p>
          
          <ol className="list-decimal pl-6 space-y-3 text-gray-600">
            <li><strong>Document the issue</strong>: Take screenshots or save messages related to the concern</li>
            <li><strong>Report immediately</strong>: Use the "Report" button available on profiles and in messages</li>
            <li><strong>Provide details</strong>: Include specifics about the incident to help our team investigate</li>
            <li><strong>Avoid escalation</strong>: Do not engage further with the person if you feel uncomfortable</li>
          </ol>
          
          <div className="bg-blue-50 p-4 rounded-md mt-6">
            <p className="text-blue-700">
              Our safety team reviews all reports within 24 hours and takes appropriate action based on our investigation.
              For urgent safety concerns, please contact your local authorities first, then notify our team.
            </p>
          </div>
        </section>
        
        <Separator className="my-8" />
        
        <section className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Consequences for Violations</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Violations of these guidelines may result in:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Warnings</li>
            <li>Temporary suspension</li>
            <li>Permanent removal from the platform</li>
            <li>Cancellation of pending transactions</li>
            <li>In serious cases, referral to appropriate authorities</li>
          </ul>
        </section>
        
        <div className="bg-gray-50 p-6 rounded-lg mt-10 text-center">
          <p className="text-gray-800 font-medium mb-2">
            These guidelines are regularly updated to ensure our community remains safe and positive.
          </p>
          <p className="text-gray-600">
            Last updated: May 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafetyGuidelines;
